import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { useFieldTypes } from "../../../api/queries/field-types/useFieldTypes";
import { useDeleteFieldType } from "../../../api/queries/field-types/useDeleteFieldType";
import {
  TreeTable,
  TreeTableExpandedKeysType,
  TreeTableRowMouseEvent,
} from "primereact/treetable";
import { Column } from "primereact/column";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useQueryClient } from "react-query";
import { defaultInvalidateQueryFilters } from "../../../api/query";
import { FieldType } from "../../../api/types/field-type";
import { useContext, useState, useMemo, useEffect } from "react";
import { appToastContext } from "../../../context/toast";
import { InputText } from "primereact/inputtext";
import { TreeNode } from "primereact/treenode";
import Pagination from "../common/Pagination";

export default function FieldTypesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { ref: appToastRef } = useContext(appToastContext);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<TreeTableExpandedKeysType>(
    {},
  );

  const { data: fieldTypesResponse, isLoading } = useFieldTypes({
    page: page + 1, // API uses 1-based pagination
    limit: rowsPerPage,
    search: searchTerm.trim(),
  });
  const deleteFieldType = useDeleteFieldType();

  // Reset to first page when search term changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // Convert tree data to TreeNodes
  useEffect(() => {
    if (!fieldTypesResponse?.data) {
      setNodes([]);
      return;
    }

    const convertToTreeNodes = (fieldTypes: any[]): TreeNode[] => {
      return fieldTypes.map((fieldType) => ({
        key: fieldType.id,
        data: fieldType,
        children: fieldType.fieldTypes
          ? convertToTreeNodes(fieldType.fieldTypes)
          : [],
      }));
    };

    const treeNodes = convertToTreeNodes(fieldTypesResponse.data);
    setNodes(treeNodes);

    // Set all nodes expanded by default
    const keys: TreeTableExpandedKeysType = {};
    const setExpandedRecursively = (nodes: TreeNode[]) => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          keys[node.key!] = true;
          setExpandedRecursively(node.children);
        }
      });
    };
    setExpandedRecursively(treeNodes);
    setExpandedKeys(keys);
  }, [fieldTypesResponse?.data]);

  // Filter tree nodes based on search term
  const filteredNodes = useMemo(() => {
    if (!searchTerm.trim()) {
      return nodes;
    }

    const filterNodes = (nodes: TreeNode[]): TreeNode[] => {
      return nodes
        .map((node) => {
          const matchesSearch = node.data.name
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
          const filteredChildren = node.children
            ? filterNodes(node.children)
            : [];

          if (matchesSearch || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        })
        .filter(Boolean) as TreeNode[];
    };

    return filterNodes(nodes);
  }, [nodes, searchTerm]);

  const handleDelete = (fieldType: FieldType) => {
    confirmDialog({
      message: `Are you sure you want to delete "${fieldType.name}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteFieldType.mutateAsync(fieldType.id);
          queryClient.invalidateQueries(
            ["field-types"],
            defaultInvalidateQueryFilters,
          );
          appToastRef?.current?.show({
            severity: "success",
            summary: "Success",
            detail: `Field type "${fieldType.name}" has been deleted successfully`,
          });
        } catch (error) {
          appToastRef?.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete field type. Please try again.",
          });
        }
      },
    });
  };

  const onRowClick = ({ node }: TreeTableRowMouseEvent) => {
    navigate(`/crm/field-types/${node.data.id}`);
  };

  const actionBodyTemplate = (node: TreeNode) => {
    const fieldType = node.data as FieldType;
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/crm/field-types/${fieldType.id}`);
          }}
          tooltip="Edit"
          tooltipOptions={{ position: "bottom" }}
        />
        <Button
          icon="pi pi-trash"
          className={`p-button-rounded p-button-text p-button-danger p-button-sm ${
            hasChildren ? "p-disabled" : ""
          }`}
          disabled={hasChildren}
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) {
              e.preventDefault();
              return;
            }
            handleDelete(fieldType);
          }}
          tooltip={
            hasChildren ? "Cannot delete: has child field types" : "Delete"
          }
          tooltipOptions={{ position: "bottom" }}
          style={hasChildren ? { pointerEvents: "auto" } : {}}
        />
      </div>
    );
  };

  const handlePageChange = (newPage: number, newRowsPerPage: number) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const totalRecords = fieldTypesResponse?.pagination?.total_count || 0;

  return (
    <div className={"col-12"}>
      <div className="flex py-3">
        <h1 className="m-0">Field Types</h1>

        <div className="flex gap-2 ml-auto align-items-center">
          <div className="p-input-icon-left">
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name..."
              className="p-inputtext-sm"
            />
          </div>
          <Button
            onClick={() => navigate("/crm/field-types/new")}
            className="p-button p-button-main p-button-sm"
            label="Create New Field Type"
          />
        </div>
      </div>

      <TreeTable
        value={filteredNodes}
        expandedKeys={expandedKeys}
        onToggle={(e) => setExpandedKeys(e.value)}
        loading={isLoading}
        emptyMessage={
          searchTerm
            ? "No field types found matching your search"
            : "No field types found"
        }
        className="p-datatable-sm p-datatable-pointer"
        stripedRows={true}
        onRowClick={onRowClick}
      >
        <Column field="name" header="Name" expander />
        <Column field="type" header="Type" />
        <Column
          field="required"
          header="Required"
          body={(node) => (node.data.required ? "Yes" : "No")}
        />
        <Column
          field="active"
          header="Active"
          body={(node) => (node.data.active ? "Yes" : "No")}
        />
        <Column
          header="Actions"
          body={actionBodyTemplate}
          style={{ width: "120px" }}
        />
      </TreeTable>

      <Pagination
        totalRecords={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      <ConfirmDialog />
    </div>
  );
}
